/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import dayjs from 'dayjs';
import { QuestionInputType } from '@/components/request/interface';
import { QuestionListFilters } from '@/generated/graphql';
import { IExceptionSelectedFilters } from '../pages/exceptions/interface';
import AppUtil from './AppUtil';

const FILTERS_LOCAL_STORAGE_KEY = 'EXCEPTION_CENTRE_LOCAL_FILTERS';

export const prepareExceptionCentreUrlParams = (
  filters: IExceptionSelectedFilters,
  activeExceptionId: string | null
): URLSearchParams => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value && value.length > 0) {
      params.append(key, value.join(','));
    }
  });

  if (activeExceptionId) {
    params.set('activeExceptionId', activeExceptionId);
  }

  return params;
};

export const getExceptionCentreFiltersFromSearchString = (
  searchString: string
): IExceptionSelectedFilters => {
  const params = new URLSearchParams(searchString);

  const filters: IExceptionSelectedFilters = {
    process: [],
    state: [],
    dateRange: [],
    assignee: [],
    typename: [],
    searchText: []
  };
  params.forEach((value, key) => {
    if (key === 'searchText') {
      filters[key] = [value];
    } else {
      (filters as any)[key] = value.split(',');
    }
  });
  return filters;
};

export const saveExceptionCentreFilters = (
  filters: IExceptionSelectedFilters
) => {
  AppUtil.setInLocalStorage(FILTERS_LOCAL_STORAGE_KEY, JSON.stringify(filters));
};

export const getExceptionCentreLocalFilters =
  (): IExceptionSelectedFilters | null => {
    const localData = AppUtil.getFromLocalStorage(FILTERS_LOCAL_STORAGE_KEY);
    if (localData) {
      return JSON.parse(localData);
    }
    return null;
  };

export const getActiveExceptionIdFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('activeExceptionId');
};

export const getDocumentExceptionFile = (uiConcepts: string): string | null => {
  const concepts = AppUtil.safeParseJSON(uiConcepts);
  const fileConcept = concepts.find((concept: any) =>
    concept.fact_types.includes('FILE')
  );
  return fileConcept?.ids ? fileConcept.ids[0] : null;
};

export const getDocumentUrlFromContextMetadata = (
  contextMetadata: string | null
): string | null => {
  if (!contextMetadata) return null;
  const conceptMetaData = AppUtil.safeParseJSON(contextMetadata);
  let s3Url = null;
  if (conceptMetaData?.fact_types?.length) {
    if (conceptMetaData.fact_types.includes('FILE')) {
      s3Url = conceptMetaData.display_values?.[0] || null;
    }
  }
  return s3Url;
};

// TODO: Move this to theme
export const URGENCY_COLOR_MAP = {
  ERROR: '#F5222D',
  URGENT_WARNING: '#FF7A45',
  WARNING: '#FAAD14'
};

/**
 * Get the color of exception urgency based on creation date
 * @param date
 * @returns
 */
export const getUrgencyColor = (date: Date) => {
  // 7+ days
  if (dayjs().diff(date, 'day') >= 7) {
    return URGENCY_COLOR_MAP.ERROR;
  }
  // 3-7 days
  if (dayjs().diff(date, 'day') >= 3) {
    return URGENCY_COLOR_MAP.URGENT_WARNING;
  }
  return URGENCY_COLOR_MAP.WARNING;
};

type ExceptionOption = {
  key: QuestionInputType;
  title: string;
};

export const EXCEPTION_OPTIONS: {
  [key in QuestionInputType]?: ExceptionOption;
} = {
  [QuestionInputType.APPROVE_REVIEW]: {
    key: QuestionInputType.APPROVE_REVIEW,
    title: 'Approve'
  },
  [QuestionInputType.VALUE]: {
    key: QuestionInputType.VALUE,
    title: 'Write in answer'
  },
  [QuestionInputType.VALUE_UPLOAD]: {
    key: QuestionInputType.VALUE_UPLOAD,
    title: 'Upload files'
  },
  [QuestionInputType.DOCUMENT_FIELDS]: {
    key: QuestionInputType.DOCUMENT_FIELDS,
    title: 'Select from document'
  },
  [QuestionInputType.VALUE_EMPTY]: {
    key: QuestionInputType.VALUE_EMPTY,
    title: 'No value'
  },
  // Hidden now
  [QuestionInputType.NO_VALUE]: {
    key: QuestionInputType.NO_VALUE,
    title: 'No value'
  },
  [QuestionInputType.SKIP]: {
    key: QuestionInputType.SKIP,
    title: 'Skip this step'
  },
  // TODO: Phase 2
  [QuestionInputType.CONCEPT]: {
    key: QuestionInputType.CONCEPT,
    title: 'Concept'
  },
  [QuestionInputType.TECHNIQUE]: {
    key: QuestionInputType.TECHNIQUE,
    title: 'Compute an answer'
  },
  // Not supported yet
  [QuestionInputType.SKIP_RUN_END]: {
    key: QuestionInputType.SKIP_RUN_END,
    title: 'Skip to end of run'
  },
  [QuestionInputType.RETRY]: {
    key: QuestionInputType.RETRY,
    title: 'Retry'
  },
  [QuestionInputType.RETRY_AFTER_INTERVAL]: {
    key: QuestionInputType.RETRY_AFTER_INTERVAL,
    title: 'Retry after an interval'
  },
  // Hidden now
  [QuestionInputType.NONE]: {
    key: QuestionInputType.NONE,
    title: 'None'
  },
  // Usually handled separately
  [QuestionInputType.VALUE_CHOICE]: {
    key: QuestionInputType.VALUE_CHOICE,
    title: 'Choose value'
  }
};

export const prepareExceptionFiltersForRequest = (
  selectedFilters: IExceptionSelectedFilters
): QuestionListFilters => {
  return {
    state:
      selectedFilters.state.length === 0 ? undefined : selectedFilters.state,
    procedureName:
      selectedFilters.process.length === 0
        ? undefined
        : selectedFilters.process,
    requestCreatedAfter: selectedFilters.dateRange?.[0] || undefined,
    requestCreatedBefore: selectedFilters.dateRange?.[1] || undefined,
    containsWords:
      selectedFilters.searchText.length === 0
        ? undefined
        : selectedFilters.searchText.join(','),
    requestAssignee:
      selectedFilters.assignee.length === 0
        ? undefined
        : selectedFilters.assignee,
    typename:
      selectedFilters.typename.length === 0
        ? undefined
        : selectedFilters.typename
  };
};
